package it.maicol07.gamerlogue.`data`

import it.maicol07.spraypaintkt.CollectionProxy
import it.maicol07.spraypaintkt.RecordProxy
import it.maicol07.spraypaintkt.Resource
import it.maicol07.spraypaintkt.ResourceRegistry
import it.maicol07.spraypaintkt.ResourceSerializer
import it.maicol07.spraypaintkt.Scope
import it.maicol07.spraypaintkt.SortDirection
import it.maicol07.spraypaintkt.extensions.DirtyMap
import it.maicol07.spraypaintkt.extensions.trackChanges
import it.maicol07.spraypaintkt.interfaces.JsonApiConfig
import it.maicol07.spraypaintkt.registerResources
import kotlin.Any
import kotlin.Boolean
import kotlin.Number
import kotlin.String
import kotlin.Unit
import kotlin.collections.Map
import kotlin.collections.MutableMap
import kotlin.reflect.KClass
import kotlinx.serialization.Serializable

@Serializable(Activity.Companion.Serializer::class)
public class Activity : Resource, ActivitySchema {
  override val `companion`: Resource.CompanionObj<Activity> = Companion

  override var id: String? = null

  override var isPersisted: Boolean = false

  override val attributes: DirtyMap<String, Any?> = mutableMapOf<String, Any?>().trackChanges()

  override val relationships: DirtyMap<String, Any?> = mutableMapOf<String, Any?>().trackChanges()

  override val meta: MutableMap<String, Any?> = mutableMapOf()

  override val links: MutableMap<String, Any?> = mutableMapOf()

  override val type: String by lazy { companion.resourceType }

  override var verb: String
    get() = if (attributes.containsKey("verb")) {
      attributes["verb"] as String
    } else {
      throw NoSuchElementException("verb not found")
    }
    set(`value`) {
      attributes["verb"] = value
    }

  override var subjectType: String?
    get() = if (attributes.containsKey("subject_type")) {
      attributes["subject_type"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["subject_type"] = value
    }

  override var payload: Map<String, Any?>
    get() = if (attributes.containsKey("payload")) {
      attributes["payload"] as Map<String, Any?>
    } else {
      super.payload
    }
    set(`value`) {
      attributes["payload"] = value
    }

  override var createdAt: String?
    get() = if (attributes.containsKey("created_at")) {
      attributes["created_at"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["created_at"] = value
    }

  override var updatedAt: String?
    get() = if (attributes.containsKey("updated_at")) {
      attributes["updated_at"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["updated_at"] = value
    }

  override var user: User?
    get() = if (relationships.containsKey("user") && (relationships["user"] == null || relationships["user"] is User?)) {
      relationships["user"] as User?
    } else {
      null
    }
    set(`value`) {
      relationships["user"] = value
    }

  public constructor(
    verb: String? = null,
    subjectType: String? = null,
    payload: Map<String, Any?>? = null,
    createdAt: String? = null,
    updatedAt: String? = null,
    user: User? = null,
  ) {
    if (verb != null) {
      attributes["verb"] = verb
    }
    if (subjectType != null) {
      attributes["subject_type"] = subjectType
    }
    if (payload != null) {
      attributes["payload"] = payload
    }
    if (createdAt != null) {
      attributes["created_at"] = createdAt
    }
    if (updatedAt != null) {
      attributes["updated_at"] = updatedAt
    }
    if (user != null) {
      relationships["user"] = user
    }
  }

  public companion object : Resource.CompanionObj<Activity> {
    override val resourceType: String = "Activity"

    override val endpoint: String = "activities"

    override val config: JsonApiConfig = AppJsonApiConfig

    override val factory: () -> Activity = { Activity() }

    override val schema: KClass<ActivitySchema> = ActivitySchema::class

    init {
      ResourceRegistry.registerResources()
    }

    public fun scope(options: Scope<Activity>.() -> Unit = {}): Scope<Activity> = Scope(Activity::class, options)

    public final suspend fun all(): CollectionProxy<Activity> = scope().all()

    public final suspend fun find(id: String): RecordProxy<Activity> = scope().find(id)

    public final suspend fun findOrNull(id: String): RecordProxy<Activity?> = scope().findOrNull(id)

    public final suspend fun first(): RecordProxy<Activity> = scope().first()

    public final suspend fun firstOrNull(): RecordProxy<Activity?> = scope().firstOrNull()

    public final suspend fun exists(): Boolean = scope().exists()

    public final suspend fun last(): RecordProxy<Activity> = scope().last()

    public final suspend fun lastOrNull(): RecordProxy<Activity?> = scope().lastOrNull()

    public final fun page(pageNumber: Number): Scope<Activity> = scope().page(pageNumber)

    public final fun per(pageSize: Number): Scope<Activity> = scope().per(pageSize)

    public final fun limit(limit: Number): Scope<Activity> = scope().limit(limit)

    public final fun offset(offset: Number): Scope<Activity> = scope().offset(offset)

    public final fun `where`(attribute: String, `value`: Any): Scope<Activity> = scope().where(attribute, value)

    public final fun extraParam(key: String, `value`: String): Scope<Activity> = scope().extraParam(key, value)

    public final fun order(attribute: String): Scope<Activity> = scope().order(attribute)

    public final fun order(attribute: String, sortDirection: SortDirection): Scope<Activity> = scope().order(attribute, sortDirection)

    public final fun includes(vararg relationships: String): Scope<Activity> = scope().includes(*relationships)

    public final fun select(type: String, vararg fields: String): Scope<Activity> = scope().select(type, *fields)

    public object Serializer : ResourceSerializer<Activity>()
  }
}
